import { useEffect, useRef, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ThumbsUp, ThumbsDown, HelpCircle, Users } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

/**
 * Phase 63 — CandidateVoteBar
 *
 * Compact voting widget shown wherever a candidate appears in the ATS.
 * Three vote types: interested / maybe / not_interested. Any recruiter,
 * employer, or interviewer with access to the profile can vote once per
 * application (tapping their own vote again clears it).
 *
 * Aggregate counts are visible to everyone; hovering the button reveals the
 * list of voters (name + role). Combined signal helps surface strong
 * profiles fast.
 *
 * Props:
 *   applicationId  (required) — the application to vote on
 *   initial        (optional) — {counts, my_vote, voters} pre-hydrated by parent
 *                                (skip the on-mount fetch)
 *   size           "sm" | "md"  — sm for lists/cards, md for detail headers
 *   onChange       (v) => void — notify parent so it can update its cached row
 *
 * Auto-fires a `pw-feed-refresh` event on change so the /feed page picks up
 * the new `vote_cast` activity in its next tick.
 */
export default function CandidateVoteBar({ applicationId, initial, size = "sm", onChange }) {
    const { user } = useAuth();
    const [summary, setSummary] = useState(initial || null);
    const [loading, setLoading] = useState(!initial);
    const [busy, setBusy] = useState(false);
    const [hover, setHover] = useState(null);

    useEffect(() => {
        let alive = true;
        if (initial) { setSummary(initial); setLoading(false); return () => {}; }
        if (!applicationId) return () => {};
        (async () => {
            try {
                const { data } = await api.get(`/applications/${applicationId}/votes`);
                if (alive) setSummary(data);
            } catch { /* silent — user may not have access */ }
            finally { if (alive) setLoading(false); }
        })();
        return () => { alive = false; };
    }, [applicationId, initial]);

    const canVote = user && ["employer", "recruiter", "interviewer"].includes(user.role);
    if (!canVote || !applicationId) return null;
    if (loading) return null;

    const cast = async (v) => {
        if (busy) return;
        setBusy(true);
        const prevSummary = summary;
        const isToggleOff = summary?.my_vote === v;
        // Optimistic UI
        setSummary((cur) => {
            if (!cur) return cur;
            const counts = { ...(cur.counts || {}) };
            if (cur.my_vote && counts[cur.my_vote] > 0) counts[cur.my_vote] -= 1;
            if (!isToggleOff) counts[v] = (counts[v] || 0) + 1;
            return { ...cur, counts, my_vote: isToggleOff ? null : v };
        });
        try {
            const { data } = isToggleOff
                ? await api.delete(`/applications/${applicationId}/vote`)
                : await api.post(`/applications/${applicationId}/vote`, { vote: v });
            setSummary({ ...(summary || {}), ...data });
            onChange?.(data);
            window.dispatchEvent(new CustomEvent("pw-feed-refresh"));
        } catch (err) {
            setSummary(prevSummary);
            toast.error(formatApiError(err));
        } finally { setBusy(false); }
    };

    const counts = summary?.counts || { interested: 0, maybe: 0, not_interested: 0 };
    const my = summary?.my_vote || null;
    const voters = summary?.voters || [];

    const BTNS = [
        { key: "interested",     Icon: ThumbsUp,   label: "Interested",     tone: "emerald",
          activeCls: "bg-emerald-600 text-white border-emerald-600 hover:bg-emerald-700",
          idleCls:   "border-border bg-white hover:border-emerald-400 hover:text-emerald-700",
        },
        { key: "maybe",          Icon: HelpCircle, label: "Maybe",          tone: "amber",
          activeCls: "bg-amber-500 text-white border-amber-500 hover:bg-amber-600",
          idleCls:   "border-border bg-white hover:border-amber-400 hover:text-amber-700",
        },
        { key: "not_interested", Icon: ThumbsDown, label: "Not interested", tone: "rose",
          activeCls: "bg-rose-600 text-white border-rose-600 hover:bg-rose-700",
          idleCls:   "border-border bg-white hover:border-rose-400 hover:text-rose-700",
        },
    ];

    const sizing = size === "md"
        ? { pad: "px-3 py-1.5 text-xs", icon: "w-4 h-4", gap: "gap-1.5" }
        : { pad: "px-2 py-1 text-[11px]", icon: "w-3.5 h-3.5", gap: "gap-1" };

    return (
        <div
            className="inline-flex items-center border border-border bg-white divide-x divide-border"
            data-testid={`vote-bar-${applicationId}`}
            onClick={(e) => e.stopPropagation()}
        >
            {BTNS.map((b) => {
                const active = my === b.key;
                const count = counts[b.key] || 0;
                return (
                    <div key={b.key} className="relative" onMouseEnter={() => setHover(b.key)} onMouseLeave={() => setHover(null)}>
                        <button
                            type="button"
                            disabled={busy}
                            onClick={() => cast(b.key)}
                            title={active ? `You voted ${b.label} — click to clear` : b.label}
                            className={`inline-flex items-center ${sizing.gap} ${sizing.pad} font-mono uppercase tracking-widest transition-colors ${active ? b.activeCls : b.idleCls} disabled:opacity-60`}
                            data-testid={`vote-btn-${b.key}-${applicationId}`}
                        >
                            <b.Icon className={sizing.icon} />
                            <span className="tabular-nums">{count}</span>
                        </button>
                        {hover === b.key && voters.filter((v) => v.vote === b.key).length > 0 && (
                            <VoterPopover voters={voters.filter((v) => v.vote === b.key)} label={b.label} />
                        )}
                    </div>
                );
            })}
        </div>
    );
}

function VoterPopover({ voters, label }) {
    return (
        <div className="absolute z-40 top-full left-1/2 -translate-x-1/2 mt-1 min-w-[180px] max-w-[280px] bg-white border border-border shadow-xl p-2.5 pointer-events-none">
            <div className="flex items-center gap-1.5 text-[9px] font-mono uppercase tracking-widest text-muted-foreground pb-1.5 border-b border-border mb-1.5">
                <Users className="w-3 h-3" /> {label} · {voters.length}
            </div>
            <ul className="space-y-0.5">
                {voters.slice(0, 8).map((v) => (
                    <li key={v.user_id} className="text-xs truncate">
                        <span className="font-medium">{v.user_name || v.user_id}</span>
                        {v.user_role && <span className="ml-1 text-[10px] font-mono uppercase text-muted-foreground">{v.user_role}</span>}
                    </li>
                ))}
                {voters.length > 8 && (
                    <li className="text-[10px] font-mono uppercase text-muted-foreground">+ {voters.length - 8} more</li>
                )}
            </ul>
        </div>
    );
}
